import Popover from 'core/components/dialogs/Popover'
import Input, { InputProps } from 'core/components/inputs/Input'
import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import dayjs, { Dayjs } from 'utils/dayjs'
import cn from 'utils/cn'
import Button from 'core/components/buttons/Button'
import Calendar from 'core/components/inputs/date-picker/Calendar'
import MonthSwitcher from 'core/components/inputs/date-picker/MonthSwitcher'
import DateInput from 'core/components/inputs/date-picker/DateInput'
import Icon from 'core/components/icons/Icon'

interface DatePickerProps extends Omit<
  InputProps,
  'onChange' | 'className' | 'value' | 'children' | 'suffix' | 'prefix' | 'type'
> {
  /**
   * The selected date value.
   */
  value?: null | undefined | Dayjs
  /**
   * Callback fired when the date value changes.
   */
  onChange?: (value: Dayjs | null) => void
  /**
   * If true, dismisses the picker after selecting a date.
   */
  dismissOnSelect?: boolean
  /**
   * Additional class names for the date picker container.
   */
  className?: string
  /**
   * Disables selection of dates after this value.
   */
  disableAfter?: Dayjs | null
  /**
   * Disables selection of dates before this value.
   */
  disableBefore?: Dayjs | null
  /**
   * Type of the picker (should be 'date-picker').
   */
  type?: 'date-picker'
  /**
   * If true, allows clearing the selected date.
   */
  allowClear?: boolean
  /**
   * If true, disables selection of today's date.
   */
  disableToday?: boolean
  /**
   * If true, hides the calendar icon.
   */
  hideIcon?: boolean
  /**
   * Optional predicate — when provided, a day is disabled if this returns true.
   * Applied in addition to `disableBefore`/`disableAfter`.
   */
  shouldDisableDate?: (d: Dayjs) => boolean
}

/**
 * A component that displays the date picker, a today button and an input to manually select a date
 * */
const DatePicker = forwardRef(
  (
    {
      onChange,
      value,
      dismissOnSelect = true,
      className,
      disableBefore,
      disableAfter,
      shouldDisableDate,
      allowClear = true,
      disableToday = false,
      readOnly,
      hideIcon,
      wrapperClassName,
      ...props
    }: DatePickerProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const [menuExpanded, setMenuExpanded] = useState(false)
    const elementRef = useRef<HTMLDivElement>(null)
    const [calendarDate, setCalendarDate] = useState<Dayjs>(dayjs())

    const onFocus = useCallback(() => {
      setMenuExpanded(!menuExpanded)
    }, [menuExpanded])

    const onBlur = useCallback(() => {
      setMenuExpanded(false)
    }, [])

    const content = useMemo(() => {
      return (
        <div
          ref={elementRef}
          className={cn(['card-shadow bg-white rounded-md w-[18rem]'])}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
        >
          <div className="h-[350px] pt-2">
            <div className="flex items-end justify-center gap-2 w-full px-3">
              <DateInput
                label="Selected date"
                date={value ? dayjs(value) : value}
                onChange={(_date) => {
                  setCalendarDate(_date)
                  onChange?.(_date)
                }}
              />

              <Button
                variant="outline"
                className="h-9 mb-4 flex items-center"
                onClick={() => {
                  setCalendarDate(dayjs())
                  onChange?.(dayjs())
                  if (dismissOnSelect) setMenuExpanded(false)
                }}
                disabled={disableToday}
              >
                Today
              </Button>
            </div>
            <div className="border-y border-dark-1 py-2 mb-2">
              <MonthSwitcher
                date={calendarDate}
                onChange={(_date) => {
                  setCalendarDate(_date)
                }}
              />
            </div>

            <Calendar
              value={value}
              onChange={(_date) => {
                if (dismissOnSelect) setMenuExpanded(false)
                if (!calendarDate.isSame(_date, 'month')) setCalendarDate(_date)

                onChange?.(_date)
              }}
              date={calendarDate}
              disableBefore={disableBefore}
              disableAfter={disableAfter}
              shouldDisableDate={shouldDisableDate}
            />
          </div>
        </div>
      )
    }, [
      value,
      disableToday,
      calendarDate,
      disableBefore,
      disableAfter,
      shouldDisableDate,
      onChange,
      dismissOnSelect
    ])

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          elementRef.current &&
          !elementRef.current.contains(event.target as Node) &&
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          event?.target?.nodeName !== 'INPUT'
        ) {
          setMenuExpanded(false)
        }
      }

      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onBlur()
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleEscapeKey)
      }
    }, [onBlur])

    useEffect(() => {
      if (!menuExpanded) {
        if (value) {
          return setCalendarDate(value as Dayjs)
        }

        setCalendarDate(dayjs())
      }
    }, [menuExpanded, value])

    const inputValue = useMemo(() => {
      if (value === null || value === undefined) return ''

      return value.format('MMM D, YYYY')
    }, [value])

    return (
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn('flex', wrapperClassName)}
        ref={ref}
      >
        <Popover
          show={menuExpanded}
          position="bottom-left"
          useArrow={false}
          content={content}
        >
          <Input
            onClick={onFocus}
            value={inputValue}
            placeholder="Select date"
            className={cn(['caret-transparent w-full', className])}
            onChange={console.log}
            autoComplete="off"
            readOnly={readOnly}
            {...props}
            suffix={
              allowClear && !readOnly ? (
                <Icon
                  name="close"
                  height={11}
                  width={11}
                  className={cn([
                    'opacity-0 hover:opacity-80 rounded-full bg-dark-3 hover:bg-dark-4',
                    value && 'opacity-100 cursor-pointer'
                  ])}
                  color="text-white"
                  onClick={() => {
                    setMenuExpanded(false)
                    setCalendarDate(dayjs())
                    onChange?.(null)
                  }}
                />
              ) : undefined
            }
            prefix={
              hideIcon ? undefined : (
                <Icon
                  name="calendar-outline"
                  height={16}
                  width={16}
                  className="mb-1"
                />
              )
            }
          />
        </Popover>
      </div>
    )
  }
)

export default DatePicker
